import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/utils/currencyUtils';
import { formatYearMonth, formatYearMonthShortRange } from '@/utils/dateUtils/dateFormatUtils';
import { DataTable, useDateFormatter } from '@acast-tech/decibel';
import { CardContent, CardShell } from './common';
import { uppercaseFirstLetter } from '@/utils/stringUtils';
import NoDataMessage from '../states/NoDataMessage';
import type { Payout, Payouts, PayoutDetails } from '@/types/api/payouts';
import { cardFullWidth } from '@/utils/styleUtils';
import { ColumnHeader } from '../tables/ColumnHeader';
import { useDerivedNetworkContext } from '@/context';
import { usePayoutsQuery } from '@/services/revenueApi';
import { handleMonthSelectTracking } from '@/utils/mixpanelUtils/trackingUtils';
import { useCallback } from 'react';
import { DateString, YearString } from '@/types/state/filter';

const PayoutsCard = ({ year }: { year: YearString }) => {
  const { selectedNetwork } = useDerivedNetworkContext();
  const monthFormatter = useDateFormatter({ month: 'long' });
  const queryResult = usePayoutsQuery(selectedNetwork.id, year);

  const handleMonthSelect = useCallback(
    (payoutDate: DateString) => handleMonthSelectTracking(payoutDate, year, monthFormatter),
    [monthFormatter, year]
  );

  return (
    <CardShell className={cardFullWidth}>
      <CardContent
        {...queryResult}
        data={queryResult.data}
        renderData={(data: Payouts) => (
          <Data data={data.items} handleMonthSelect={handleMonthSelect} />
        )}
      />
    </CardShell>
  );
};

const Data = ({
  data,
  handleMonthSelect,
}: {
  data: Payout[];
  handleMonthSelect: (payoutDate: DateString) => void;
}) => {
  const navigate = useNavigate();

  if (!data?.length) {
    return <NoDataMessage dataType="payouts" />;
  }

  const renderColumnData = (details: PayoutDetails) => {
    const allCalcPeriods: string[] = Object.values(details)
      .flatMap((category) => category.calculation_period)
      .filter((period) => period);

    if (!allCalcPeriods.length) return '';

    const calcPeriodStr = formatYearMonthShortRange(allCalcPeriods);

    return (
      <div>
        {calcPeriodStr && (
          <span className="text-xs md:text-sm text-[#8B8B8B] whitespace-normal">{`Earnings from: ${calcPeriodStr}`}</span>
        )}
      </div>
    );
  };

  const TableRowLink = ({
    payout_date,
    children,
  }: {
    payout_date: string;
    children: React.ReactNode;
  }) => {
    const month = payout_date.slice(5, 7);
    return (
      <Link
        to={month}
        onClick={(e) => {
          e.preventDefault();
          navigate(month, { relative: 'route' });
        }}
        className="hover:cursor-pointer"
      >
        {children}
      </Link>
    );
  };

  const tableColumns = [
    {
      id: 'payout_month',
      title: <ColumnHeader>Payout statement sent in</ColumnHeader>,
      width: '200px',
      sorter: ({ payout_date }: Payout) => payout_date,
      render: ({ payout_date, details }: Payout) => (
        <TableRowLink payout_date={payout_date}>
          <span className="text-textDarkGrey" data-testid="payout-month">
            <span className="font-medium hover:underline hover:cursor-pointer">
              {formatYearMonth(payout_date)}
            </span>

            <span className="font-light">{renderColumnData(details)}</span>
          </span>
        </TableRowLink>
      ),
      order: 'desc',
    },
    ...(['ads', 'programmatic', 'sponsorships', 'branded_content', 'social', 'other'] as const).map(
      (key) => ({
        id: key,
        title: <ColumnHeader>{uppercaseFirstLetter(key.split('_')[0])}</ColumnHeader>,
        sorter: false,
        width: '100px',
        render: ({ details, payout_date }: Payout) => {
          return (
            <TableRowLink payout_date={payout_date}>
              <span className="font-light text-textDarkGrey">
                {formatCurrency(details[key].total.amount, details[key].total.currency)}
              </span>
            </TableRowLink>
          );
        },
      })
    ),
    {
      id: 'total',
      title: <ColumnHeader>Total revenue</ColumnHeader>,
      align: 'right' as const,
      sorter: false,
      width: '125px',
      render: ({ total, payout_date }: Payout) => {
        return (
          <TableRowLink payout_date={payout_date}>
            <span className="font-bold text-textDarkGrey" data-testid="payout-total">
              {formatCurrency(total.amount, total.currency)}
            </span>
          </TableRowLink>
        );
      },
    },
  ];

  const getMonth = (payoutDate: string) => {
    return payoutDate.slice(5, 7);
  };

  return (
    <DataTable
      title="payouts"
      fixedLayout
      columns={tableColumns}
      data={data}
      getRowId={(row: Payout) => JSON.stringify(row)}
      scrollable={true}
      pagination={false}
      onRowClick={(row: Payout) => {
        handleMonthSelect(row.payout_date);
        navigate(getMonth(row.payout_date), { relative: 'route' });
      }}
    />
  );
};

export default PayoutsCard;
